import { useState, useEffect, useRef } from "react";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Database, RefreshCw, ArrowRight, AlertTriangle, CheckCircle2 } from "lucide-react";

const BACKEND_STYLES = {
  mongo:    { color: "bg-[#F3F2EE] text-[#1A231C]",    label: "MongoDB",    next: "dual" },
  dual:     { color: "bg-[#FFF3D6] text-[#5C4400]",    label: "Dual Write", next: "postgres" },
  postgres: { color: "bg-[#DDF0E0] text-[#1F4D2C]",    label: "Postgres",   next: "mongo" },
};

const DRIFT_STYLES = {
  ok:       { color: "text-[#1F4D2C]", label: "✓ in sync"   },
  warn:     { color: "text-[#8A6500]", label: "⚠ drifting"  },
  critical: { color: "text-[#B91C1C]", label: "✗ DRIFT"     },
  unknown:  { color: "text-[#8A998E]", label: "—"           },
};

export default function MigrationStatusTab() {
  const [rows, setRows] = useState([]);
  const [drift, setDrift] = useState({});
  const [pgAvailable, setPgAvailable] = useState(true);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [flipping, setFlipping] = useState(null);
  const driftTimerRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/migration-status`);
      setRows(r.data.rows || []);
      setPgAvailable(!!r.data.pg_available);
      setMessage(r.data.message || "");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load migration status");
    } finally {
      setLoading(false);
    }
  };

  const loadDrift = async () => {
    try {
      const r = await api.get(`/admin/migration-status/drift`);
      if (!r.data.pg_available) return;
      const map = {};
      (r.data.rows || []).forEach((d) => { map[d.collection] = d; });
      setDrift(map);
    } catch (_e) {
      // Silent — drift is best-effort
    }
  };

  useEffect(() => {
    load();
    loadDrift();
    // Poll drift every 5 minutes (300s) — cheap COUNT(*) queries
    driftTimerRef.current = setInterval(loadDrift, 300_000);
    return () => clearInterval(driftTimerRef.current);
  }, []);

  const flip = async (collection, backend) => {
    const human = BACKEND_STYLES[backend]?.label || backend;
    if (!window.confirm(`Flip "${collection}" to ${human}?\n\nThis takes effect immediately — no API restart needed.`)) return;
    setFlipping(collection);
    try {
      await api.post(`/admin/migration-status/${collection}`, { backend });
      toast.success(`${collection} → ${human}`);
      await load();
      loadDrift();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Flip failed");
    } finally {
      setFlipping(null);
    }
  };

  const counts = rows.reduce((acc, r) => {
    acc[r.backend] = (acc[r.backend] || 0) + 1;
    return acc;
  }, {});

  const driftAlertCount = Object.values(drift).filter(
    (d) => d.severity === "warn" || d.severity === "critical"
  ).length;

  return (
    <div data-testid="admin-migration-status">
      <div className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-[#1A231C] font-['Outfit'] flex items-center gap-2">
            <Database className="w-5 h-5" /> Database Migration Status
          </h2>
          <p className="text-sm text-[#8A998E] mt-1">
            Per-collection routing between MongoDB and PostgreSQL. Flip takes effect immediately — no restart needed.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {driftAlertCount > 0 ? (
            <span className="inline-flex items-center gap-1 text-sm text-[#8A6500]" data-testid="drift-alert-summary">
              <AlertTriangle className="w-4 h-4" /> {driftAlertCount} drifting
            </span>
          ) : pgAvailable && Object.keys(drift).length > 0 ? (
            <span className="inline-flex items-center gap-1 text-sm text-[#1F4D2C]" data-testid="drift-alert-summary">
              <CheckCircle2 className="w-4 h-4" /> All in sync
            </span>
          ) : null}
          <Button variant="outline" size="sm"
            onClick={() => { load(); loadDrift(); }}
            disabled={loading} data-testid="migration-refresh-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} /> Refresh
          </Button>
        </div>
      </div>

      {!pgAvailable && (
        <div className="mb-6 p-4 rounded-lg bg-[#FFF3D6] border border-[#E5D5A0]" data-testid="migration-pg-warning">
          <div className="font-semibold text-[#5C4400]">PostgreSQL not configured on this server</div>
          <div className="text-sm text-[#5C4400] mt-1">{message || "Set DATABASE_URL in /var/www/api/.env and restart the API."}</div>
        </div>
      )}

      {pgAvailable && rows.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          {["mongo", "dual", "postgres"].map((b) => (
            <div key={b} className={`p-4 rounded-lg ${BACKEND_STYLES[b].color}`} data-testid={`migration-count-${b}`}>
              <div className="text-xs uppercase tracking-wider opacity-70">{BACKEND_STYLES[b].label}</div>
              <div className="text-2xl font-bold mt-1">{counts[b] || 0}</div>
              <div className="text-xs opacity-70 mt-1">collections</div>
            </div>
          ))}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-[#E5E1DA]">
        <table className="w-full text-sm">
          <thead className="bg-[#F3F2EE] text-[#1A231C]">
            <tr>
              <th className="text-left px-4 py-3 font-semibold">Collection</th>
              <th className="text-left px-4 py-3 font-semibold">Current Backend</th>
              <th className="text-left px-4 py-3 font-semibold">Mongo / PG</th>
              <th className="text-left px-4 py-3 font-semibold">Drift</th>
              <th className="text-left px-4 py-3 font-semibold">Last Synced</th>
              <th className="text-right px-4 py-3 font-semibold">Action</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan="6" className="text-center py-6 text-[#8A998E]">Loading…</td></tr>
            )}
            {!loading && rows.length === 0 && (
              <tr><td colSpan="6" className="text-center py-6 text-[#8A998E]">No rows in _migration_status table.</td></tr>
            )}
            {rows.map((r) => {
              const style = BACKEND_STYLES[r.backend] || BACKEND_STYLES.mongo;
              const nextBackend = style.next;
              const nextStyle = BACKEND_STYLES[nextBackend];
              const d = drift[r.collection];
              const dStyle = d ? DRIFT_STYLES[d.severity] : DRIFT_STYLES.unknown;
              return (
                <tr key={r.collection} className="border-t border-[#E5E1DA] hover:bg-[#FAFAF7]"
                  data-testid={`migration-row-${r.collection}`}>
                  <td className="px-4 py-3 font-mono text-[#1A231C]">{r.collection}</td>
                  <td className="px-4 py-3">
                    <Badge className={`${style.color} border-0`}>{style.label}</Badge>
                  </td>
                  <td className="px-4 py-3 text-[#8A998E] text-xs whitespace-nowrap"
                    data-testid={`migration-counts-${r.collection}`}>
                    {d ? (
                      <span>
                        {d.mongo_count >= 0 ? d.mongo_count.toLocaleString() : "—"}
                        {" / "}
                        {d.pg_count >= 0 ? d.pg_count.toLocaleString() : "—"}
                      </span>
                    ) : "—"}
                  </td>
                  <td className={`px-4 py-3 text-xs font-semibold ${dStyle.color}`}
                    data-testid={`migration-drift-${r.collection}`}>
                    {d && d.drift_pct !== null
                      ? `${dStyle.label} ${d.drift_pct > 0 ? `(${d.drift_pct}%)` : ""}`
                      : dStyle.label}
                  </td>
                  <td className="px-4 py-3 text-[#8A998E] text-xs">
                    {r.last_migrated_at ? new Date(r.last_migrated_at).toLocaleString() : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-2">
                      <Button size="sm" variant="outline"
                        disabled={!pgAvailable || flipping === r.collection || r.backend === nextBackend}
                        onClick={() => flip(r.collection, nextBackend)}
                        data-testid={`migration-flip-${r.collection}-to-${nextBackend}`}>
                        <ArrowRight className="w-3 h-3 mr-1" /> {nextStyle.label}
                      </Button>
                      {r.backend !== "mongo" && (
                        <Button size="sm" variant="ghost" className="text-red-600 hover:bg-red-50"
                          disabled={flipping === r.collection}
                          onClick={() => flip(r.collection, "mongo")}
                          data-testid={`migration-flip-${r.collection}-rollback`}>
                          Rollback
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-xs text-[#8A998E]">
        <strong>How it works:</strong> The adapter reads <code>_migration_status</code> at startup and after every flip.
        <code className="mx-1">mongo</code> → only MongoDB.
        <code className="mx-1">dual</code> → write to both, read from MongoDB (safe burn-in).
        <code className="mx-1">postgres</code> → only Postgres reads (writes still go to both).
        <strong className="ml-3">Drift</strong> = |mongo − pg| / max. Auto-refreshed every 5 min.
        <span className="text-[#1F4D2C]"> ≤ 1% = in sync</span>,
        <span className="text-[#8A6500]"> ≤ 5% = drifting</span>,
        <span className="text-[#B91C1C]"> &gt; 5% = critical</span>.
      </div>
    </div>
  );
}
